import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

import { Header } from "@/components/site/Header";
import { DenverHero } from "@/components/site/DenverHero";
import { Services } from "@/components/site/Services";
import { DenverServiceArea } from "@/components/site/DenverServiceArea";
import { Gallery } from "@/components/site/Gallery";
import { QuoteCTABanner } from "@/components/site/QuoteCTABanner";
import { GoogleReviews } from "@/components/site/GoogleReviews";
import { FAQ } from "@/components/site/FAQ";
import { Footer } from "@/components/site/Footer";
import { StickyMobileCTA } from "@/components/site/StickyMobileCTA";
import { YouTubeVideos } from "@/components/site/YouTubeVideos";
import { QuotePopup } from "@/components/site/QuotePopup";

import { DENVER_FAQS } from "@/lib/denver";
import { COMPANY } from "@/lib/constants";

const CANONICAL = "https://alldaypowerwashing.com/denver";

const DENVER_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${CANONICAL}#webpage`,
      "url": CANONICAL,
      "name": "Pressure Washing Denver, CO | All Day Power Washing",
      "description":
        "Heavy-duty pressure washing, concrete cleaning, fleet & kitchen hood cleaning across the Denver metro. Veteran owned. Licensed & insured. Same-week quotes. Call 719-644-6105.",
      "inLanguage": "en-US",
      "isPartOf": { "@id": "https://alldaypowerwashing.com/#website" },
      "about": { "@id": "https://alldaypowerwashing.com/#business" },
      "breadcrumb": { "@id": `${CANONICAL}#breadcrumb` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://alldaypowerwashing.com/" },
        { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": "https://alldaypowerwashing.com/#services" },
        { "@type": "ListItem", "position": 3, "name": "Denver", "item": CANONICAL },
      ],
    },
    {
      "@type": "Service",
      "@id": `${CANONICAL}#service`,
      "name": "Pressure Washing in Denver, Colorado",
      "serviceType": "Pressure washing, concrete cleaning, gutter cleaning, house washing, fleet cleaning, kitchen hood cleaning",
      "provider": { "@id": "https://alldaypowerwashing.com/#business" },
      "areaServed": [
        { "@type": "City", "name": "Denver" },
        { "@type": "City", "name": "Aurora" },
        { "@type": "City", "name": "Lakewood" },
        { "@type": "City", "name": "Centennial" },
        { "@type": "City", "name": "Littleton" },
        { "@type": "City", "name": "Parker" },
        { "@type": "City", "name": "Castle Rock" },
        { "@type": "City", "name": "Highlands Ranch" },
        { "@type": "City", "name": "Arvada" },
        { "@type": "City", "name": "Westminster" },
        { "@type": "City", "name": "Thornton" },
        { "@type": "City", "name": "Englewood" },
        { "@type": "City", "name": "Greenwood Village" },
        { "@type": "City", "name": "Broomfield" },
        { "@type": "City", "name": "Golden" },
        { "@type": "City", "name": "Wheat Ridge" },
        { "@type": "City", "name": "Commerce City" },
        { "@type": "City", "name": "Brighton" },
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "65",
        "bestRating": "5",
        "worstRating": "1",
      },
    },
    {
      "@type": "FAQPage",
      "@id": `${CANONICAL}#faq`,
      "mainEntity": DENVER_FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    },
  ],
};

export default function DenverLanding() {
  return (
    <div className="App" data-testid="denver-landing">
      <Helmet>
        <title>Pressure Washing Denver, CO | All Day Power Washing</title>
        <meta
          name="description"
          content="Heavy-duty pressure washing in the Denver metro — homes, fleets, commercial lots, kitchen hoods. Veteran owned. 4.9★ on Google. Call 719-644-6105."
        />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Pressure Washing Denver, CO | All Day Power Washing" />
        <meta
          property="og:description"
          content="Heavy-duty pressure washing in the Denver metro. Veteran owned. 4.9★ Google. Same-week quotes. Call 719-644-6105."
        />
        <meta property="og:url" content={CANONICAL} />
        <meta name="twitter:title" content="Pressure Washing Denver, CO | All Day Power Washing" />
        <meta
          name="twitter:description"
          content="Heavy-duty pressure washing in the Denver metro. 4.9★ Google. Call 719-644-6105."
        />
        <script type="application/ld+json">{JSON.stringify(DENVER_LD)}</script>
      </Helmet>

      <Header />

      {/* Breadcrumb back-link */}
      <div className="pt-24 pb-2 bg-[#0B1726]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors font-heading uppercase text-xs tracking-widest"
          >
            <ChevronLeft className="w-4 h-4" /> All service areas
          </Link>
        </div>
      </div>

      <DenverHero />
      <Gallery />
      <YouTubeVideos />
      <Services />
      <DenverServiceArea />
      <QuoteCTABanner />
      <GoogleReviews />
      <FAQ faqs={DENVER_FAQS} heading="Denver FAQ" accent="Answered." />
      <Footer />
      <StickyMobileCTA />
      <QuotePopup />
    </div>
  );
}
